import { FieldPath, FieldValues, ControllerRenderProps } from "react-hook-form";
import CountryDialcodeSelectionControlled from "@/components/form/CountryDialcodeSelectionControlled";
import { useMemo } from "react";
import FormItem from "@/components/ui/Form/FormItem";
import { type TypeAttributes } from "@/components/ui/@types/common";
import InputGroup from "@/components/ui/InputGroup/InputGroup";
import { NumericInput } from "../shared";

/**
 * Shown when the field is empty. Most numbers we send SMS to are Danish, so
 * pre-selecting this saves picking from a 200 entry list every time.
 *
 * It is a DISPLAY default only - it is never written into the form value. An
 * untouched field still submits "", so nothing can end up storing a bare dial
 * code as if it were a phone number. As soon as a digit is typed, the value
 * becomes "<dial code> <number>" as usual.
 */
const DEFAULT_DIAL_CODE = "+45";

export default function PhoneNumberFormItemsControlled<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(p: {
  field: ControllerRenderProps<TFieldValues, TName>;
  label: string;
  size?: TypeAttributes.ControlSize;
  required?: boolean;
  errorMessage?: string
  invalid?: boolean,
  disabled?: boolean,
  countryPlaceholder?: string|null; // defaults to Country
  numberPlaceholder?: string|null; // defaults to Number
  defaultDialCode?: string|null; // defaults to DEFAULT_DIAL_CODE. Pass "" for no preselection.
}) {
  const fullValue = p.field.value ?? "";
  const defaultDialCode = p.defaultDialCode ?? DEFAULT_DIAL_CODE;

  const dialCode = useMemo(() => {
    const split = fullValue.split(" ");
    if (split.length > 0 && split[0].startsWith("+")) {
      return split[0].trim();
    }
    // Nothing stored yet - show the default country without committing it.
    return fullValue.trim().length === 0 ? defaultDialCode : "";
  }, [fullValue, defaultDialCode]);

  const walue = useMemo(() => {
    return fullValue.replace(dialCode, "").trim();
  }, [fullValue, dialCode]);

  // Memoize props passed to dial code selector
  const dialCodeField = useMemo(
    () => ({
      value: dialCode,
      onChange: (newDial: string) => {
        const trimmed = (newDial ?? "").trim();
        p.field.onChange(`${trimmed} ${walue}`.trim());
      },
    }),
    [dialCode, walue, p.field],
  );

  return (
    <>
      <FormItem
        asterisk={p.required ?? false}
        label={p.label}
        invalid={p.invalid}
        errorMessage={p.errorMessage}
      >
        <InputGroup className="mb-4 w-full" size={"sm"}>
          <CountryDialcodeSelectionControlled
            disabled={p.disabled === true}
            size={p.size}
            field={dialCodeField}
            countryPlaceholder={p.countryPlaceholder??"Country"}
          />
          <div className={"ml-1"} />
          <NumericInput
            // @ts-expect-error Some deep css bullshit
            size={p.size}
            disabled={p.disabled === true}
            autoComplete="off"
            placeholder={p.numberPlaceholder??"Number"}
            value={walue}
            onChange={(ev) => {
              const newVal = ev.target.value ?? "";
              p.field.onChange(`${dialCode} ${newVal}`.trim());
            }}
            onBlur={p.field.onBlur}
          />
        </InputGroup>
      </FormItem>

      {
        //p.field.value
      }
    </>
  );
}
